import { Link } from 'react-router-dom'
import { ArrowRight, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ProductCard } from '@/components/product/ProductCard'
import { useFeaturedProducts, useTrendingProducts, useNewArrivals } from '@/api/products'
import { useFeaturedBrands } from '@/api/brands'
import { useState, useEffect } from 'react'
import type { Product } from '@/types'

const HERO_SLIDES = [
  'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1600&h=900&fit=crop&q=85',
  'https://images.unsplash.com/photo-1617137968427-85924c800a22?w=1600&h=900&fit=crop&q=85',
  'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1600&h=900&fit=crop&q=85',
  'https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=1600&h=900&fit=crop&q=85',
  'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600&h=900&fit=crop&q=85',
]

const HERO_CATEGORIES = [
  {
    label: 'Women', to: '/women', accent: '#d4952a',
    image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&h=600&fit=crop&q=80',
  },
  {
    label: 'Men', to: '/men', accent: '#9ca3af',
    image: 'https://images.unsplash.com/photo-1617137968427-85924c800a22?w=600&h=600&fit=crop&q=80',
  },
  {
    label: 'Beauty', to: '/beauty', accent: '#e8b84b',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&h=600&fit=crop&q=80',
  },
  {
    label: 'Home', to: '/homeware', accent: '#d4952a',
    image: 'https://images.unsplash.com/photo-1615529182904-14819c35db37?w=600&h=600&fit=crop&q=80',
  },
]

const BRAND_NAMES = [
  'John Lewis', 'Harrods', 'Burberry', 'Ralph Lauren',
  'Marks & Spencer', 'Ted Baker', 'Reiss', 'Fenwick',
  'Mulberry', 'AllSaints', 'Paul Smith', 'Hugo Boss',
]

/* Shared horizontal gutter — matches header & footer exactly */
const GUTTER = 'px-6 sm:px-10 md:px-16 lg:px-24 xl:px-32'

/* ── Seed products shown before real data loads ───────────────────────────── */
function makeProduct(
  id: string,
  name: string,
  brandName: string,
  brandSlug: string,
  price: number,
  salePrice: number | null,
  image: string,
  category: Product['category'],
  flags: { editors?: boolean; trending?: boolean; newArrival?: boolean } = {}
): Product {
  return {
    id,
    brandId: `brand-${brandSlug}`,
    brand: { id: `brand-${brandSlug}`, slug: brandSlug, name: brandName, logoUrl: null, heroImageUrl: null, tagline: null, description: null, network: 'awin', merchantId: '', isActive: true, featuredRank: null, createdAt: '' },
    slug: id,
    name,
    description: null,
    category,
    subcategory: null,
    priceGbp: price,
    salePriceGbp: salePrice,
    currency: 'GBP',
    imageUrls: [image],
    affiliateUrl: '#',
    linkId: id,
    inStock: true,
    sizes: [],
    colours: [],
    material: null,
    tags: [],
    isEditorsPick: flags.editors ?? false,
    isTrending: flags.trending ?? false,
    isNewArrival: flags.newArrival ?? false,
    clickCount: 0,
    conversionCount: 0,
    createdAt: '',
    updatedAt: '',
  }
}

/* All images below are visually verified white/near-white studio background product shots */
const W = {
  watch:    'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=750&fit=crop&q=80',
  sunglass: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=600&h=750&fit=crop&q=80',
  tshirt:   'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=600&h=750&fit=crop&q=80',
  pinkbag:  'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=600&h=750&fit=crop&q=80',
  darkshoe: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=600&h=750&fit=crop&q=80',
  lotion:   'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=600&h=750&fit=crop&q=80',
  leathbag: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=600&h=750&fit=crop',
  trainer:  'https://images.pexels.com/photos/2529148/pexels-photo-2529148.jpeg?auto=compress&cs=tinysrgb&w=600&h=750&fit=crop',
  whiteshoe:'https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=600&h=750&fit=crop',
  denim:    'https://images.pexels.com/photos/4210866/pexels-photo-4210866.jpeg?auto=compress&cs=tinysrgb&w=600&h=750&fit=crop',
}

const SEED_EDITORS: Product[] = [
  makeProduct('s1',  'Silk Midi Dress',           'Reiss',           'reiss',            295, 195, W.tshirt,    'women-fashion', { editors: true }),
  makeProduct('s2',  'Tailored Wool Blazer',       'Paul Smith',      'paul-smith',       450, null,W.denim,     'men-fashion',   { editors: true }),
  makeProduct('s3',  'Tan Leather Tote',           'Mulberry',        'mulberry',         695, null,W.leathbag,  'accessories',   { editors: true }),
  makeProduct('s4',  'White Leather Sneaker',      'John Lewis',      'john-lewis',       129, 89,  W.whiteshoe, 'footwear',      { editors: true }),
  makeProduct('s5',  'Rose Gold Watch',            'Ted Baker',       'ted-baker',        249, null,W.watch,     'accessories',   { editors: true }),
  makeProduct('s6',  'Hydrating Face Serum',       'Harrods',         'harrods',          185, null,W.lotion,    'beauty',        { editors: true }),
  makeProduct('s7',  'Chain Strap Crossbody',      'Marks & Spencer', 'marks-and-spencer',199, null,W.pinkbag,   'accessories',   { editors: true }),
  makeProduct('s8',  'Oxford Stripe Shirt',        'Charles Tyrwhitt','charles-tyrwhitt', 89,  59,  W.tshirt,    'men-fashion',   { editors: true }),
  makeProduct('s9',  'Classic Sunglasses',         'Fenwick',         'fenwick',          125, null,W.sunglass,  'accessories',   { editors: true }),
  makeProduct('s10', 'Running Trainer',            'AllSaints',       'allsaints',        129, null,W.trainer,   'footwear',      { editors: true }),
]

const SEED_NEW: Product[] = [
  makeProduct('n1',  'Linen Wide-Leg Trousers',    'Reiss',           'reiss',            195, null,W.denim,     'women-fashion', { newArrival: true }),
  makeProduct('n2',  'Merino Polo Shirt',           'Paul Smith',      'paul-smith',       145, null,W.tshirt,    'men-fashion',   { newArrival: true }),
  makeProduct('n3',  'Suede Chelsea Boot',          'Clarks',          'clarks',           189, null,W.darkshoe,  'footwear',      { newArrival: true }),
  makeProduct('n4',  'Structured Crossbody',        'Ted Baker',       'ted-baker',        229, null,W.pinkbag,   'accessories',   { newArrival: true }),
  makeProduct('n5',  'Aviator Sunglasses',          'John Lewis',      'john-lewis',       95,  null,W.sunglass,  'accessories',   { newArrival: true }),
  makeProduct('n6',  'Retinol Night Serum',         'Harrods',         'harrods',          95,  null,W.lotion,    'beauty',        { newArrival: true }),
  makeProduct('n7',  'Leather Messenger Bag',       'Mulberry',        'mulberry',         485, null,W.leathbag,  'accessories',   { newArrival: true }),
  makeProduct('n8',  'Slim Fit Chino',              'Hugo Boss',       'hugo-boss',        89,  null,W.denim,     'men-fashion',   { newArrival: true }),
  makeProduct('n9',  'Statement Watch',             'Paul Smith',      'paul-smith',       285, null,W.watch,     'accessories',   { newArrival: true }),
  makeProduct('n10', 'White Court Shoe',            'Burberry',        'burberry',         295, null,W.whiteshoe, 'footwear',      { newArrival: true }),
]

const SEED_TRENDING: Product[] = [
  makeProduct('t1',  'Chronograph Watch',          'Ted Baker',       'ted-baker',        199, null,W.watch,     'accessories',   { trending: true }),
  makeProduct('t2',  'Leather Tote',               'John Lewis',      'john-lewis',       159, null,W.leathbag,  'accessories',   { trending: true }),
  makeProduct('t3',  'Lightweight Trainer',        'Paul Smith',      'paul-smith',       155, null,W.trainer,   'footwear',      { trending: true }),
  makeProduct('t4',  'Vitamin C Serum',            'Harrods',         'harrods',          75,  null,W.lotion,    'beauty',        { trending: true }),
  makeProduct('t5',  'Straight-Leg Jean',          'Marks & Spencer', 'marks-and-spencer',49,  null,W.denim,     'women-fashion', { trending: true }),
  makeProduct('t6',  'White Leather Sneaker',      'Reiss',           'reiss',            155, null,W.whiteshoe, 'footwear',      { trending: true }),
  makeProduct('t7',  'Mini Shoulder Bag',          'Fenwick',         'fenwick',          189, 139,W.pinkbag,   'accessories',   { trending: true }),
  makeProduct('t8',  'Oversized Tee',              'AllSaints',       'allsaints',        55,  null,W.tshirt,    'men-fashion',   { trending: true }),
  makeProduct('t9',  'Sport Running Shoe',         'Clarks',          'clarks',           110, null,W.darkshoe,  'footwear',      { trending: true }),
  makeProduct('t10', 'Round Sunglasses',           'Reiss',           'reiss',            99,  null,W.sunglass,  'accessories',   { trending: true }),
]

export function HomePage() {
  const { data: featured  } = useFeaturedProducts(10)
  const { data: trending  } = useTrendingProducts(10)
  const { data: newArrivals } = useNewArrivals(10)
  const { data: brands    } = useFeaturedBrands(6)

  const marqueeNames = brands?.length ? brands.map(b => b.name) : BRAND_NAMES

  const displayFeatured  = featured?.length  ? featured   : SEED_EDITORS
  const displayNew       = newArrivals?.length ? newArrivals : SEED_NEW
  const displayTrending  = trending?.length  ? trending   : SEED_TRENDING

  const [activeSlide, setActiveSlide] = useState(0)
  const [prevSlide, setPrevSlide] = useState<number | null>(null)
  const [transitioning, setTransitioning] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setTransitioning(true)
      setPrevSlide(activeSlide)
      setActiveSlide(i => (i + 1) % HERO_SLIDES.length)
      setTimeout(() => {
        setPrevSlide(null)
        setTransitioning(false)
      }, 1200)
    }, 5000)
    return () => clearInterval(interval)
  }, [activeSlide])

  return (
    <main className="pt-[calc(2.5rem+4rem)]">

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="relative h-[88vh] min-h-[640px] flex items-end overflow-hidden">
        {prevSlide !== null && (
          <img
            key={`prev-${prevSlide}`}
            src={HERO_SLIDES[prevSlide]}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: transitioning ? 0 : 1, transition: 'opacity 1.2s ease' }}
          />
        )}
        <img
          key={`active-${activeSlide}`}
          src={HERO_SLIDES[activeSlide]}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 1 }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(10,31,10,0.55)' }} />
        <div className="absolute inset-0 opacity-20"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 60% 40%, #d4952a22 0%, transparent 70%)' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f0a] via-transparent to-transparent" />

        <div className="absolute bottom-8 right-8 flex gap-2 z-10">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => { setPrevSlide(activeSlide); setActiveSlide(i) }}
              aria-label={`Slide ${i + 1}`}
              className="rounded-full transition-all duration-300"
              style={{
                background: i === activeSlide ? '#d4952a' : 'rgba(240,235,224,0.35)',
                width: i === activeSlide ? '1.5rem' : '0.375rem',
                height: '0.375rem',
              }}
            />
          ))}
        </div>

        <div className={`relative w-full ${GUTTER} pb-24`}>
          <p className="text-xs font-body tracking-[0.35em] uppercase text-[#d4952a] mb-5">
            New Season · Spring 2025
          </p>
          <h1 className="font-display text-5xl md:text-7xl font-light text-[#f0ebe0] leading-[1.05] mb-7 max-w-2xl">
            The Art of<br />Effortless Luxury
          </h1>
          <p className="text-base text-[#9ca3af] font-body max-w-md mb-10 leading-relaxed">
            Discover curated edits from John Lewis, Harrods, Marks &amp; Spencer and Britain's
            most beloved heritage brands — delivered to your door.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button asChild size="lg" variant="gold">
              <Link to="/new-in">Shop New In <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/brands">Explore Brands</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Category tiles ────────────────────────────────────── */}
      <section className="bg-[#06090c] py-16">
        <div className={`w-full ${GUTTER}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {HERO_CATEGORIES.map(({ label, to, accent, image }) => (
              <Link key={to} to={to} className="group relative aspect-square overflow-hidden rounded-sm">
                <img
                  src={image}
                  alt={label}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-1/2 opacity-0 group-hover:opacity-30 transition-opacity duration-500"
                  style={{ background: `radial-gradient(ellipse at 50% 100%, ${accent}, transparent 70%)` }} />
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                  <span className="font-display text-xl text-white drop-shadow">{label}</span>
                  <ChevronRight className="h-4 w-4 text-[#d4952a] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Brand marquee ─────────────────────────────────────── */}
      <section className="bg-[#06090c] border-y border-white/5 py-12 overflow-hidden">
        <p className="text-[10px] font-body tracking-[0.4em] uppercase text-[#6b7280] text-center mb-8">
          As featured from
        </p>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #06090c 50%, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-28 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #06090c 50%, transparent)' }} />
          <div
            className="flex items-center"
            style={{ animation: 'marquee 36s linear infinite' }}
            onMouseEnter={e => (e.currentTarget.style.animationPlayState = 'paused')}
            onMouseLeave={e => (e.currentTarget.style.animationPlayState = 'running')}
          >
            {[...marqueeNames, ...marqueeNames].map((name, i) => (
              <span key={`${name}-${i}`} className="flex-shrink-0 flex items-center">
                <Link
                  to={`/brands/${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="opacity-40 hover:opacity-100 transition-opacity duration-300"
                >
                  <span className="font-display text-2xl md:text-3xl text-[#f0ebe0] tracking-[0.2em] uppercase whitespace-nowrap">
                    {name}
                  </span>
                </Link>
                <span className="px-10 text-[#d4952a]/40 text-lg select-none" aria-hidden="true">|</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Editor's Picks ── white ────────────────────────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className={`w-full ${GUTTER}`}>
          <div className="flex items-end justify-between mb-12 pb-5 border-b border-[#e8e8e8]">
            <div>
              <p className="text-[10px] font-body tracking-[0.35em] uppercase text-[#d4952a] mb-2">Handpicked</p>
              <h2 className="font-display text-3xl md:text-4xl font-light text-[#111]">Editor's Picks</h2>
            </div>
            <Link to="/edit/editors-picks" className="hidden md:flex items-center gap-1.5 text-[10px] font-body tracking-[0.2em] uppercase text-[#6b7280] hover:text-[#111] transition-colors">
              View All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {displayFeatured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ── New Arrivals ── off-white ─────────────────────────── */}
      <section className="bg-[#f8f7f5] py-20 md:py-28 border-t border-[#ebebeb]">
        <div className={`w-full ${GUTTER}`}>
          <div className="flex items-end justify-between mb-12 pb-5 border-b border-[#e8e8e8]">
            <div>
              <p className="text-[10px] font-body tracking-[0.35em] uppercase text-[#d4952a] mb-2">Just Landed</p>
              <h2 className="font-display text-3xl md:text-4xl font-light text-[#111]">New Arrivals</h2>
            </div>
            <Link to="/new-in" className="hidden md:flex items-center gap-1.5 text-[10px] font-body tracking-[0.2em] uppercase text-[#6b7280] hover:text-[#111] transition-colors">
              See All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {displayNew.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ── Trending ── white ──────────────────────────────────── */}
      <section className="bg-white py-20 md:py-28 border-t border-[#ebebeb]">
        <div className={`w-full ${GUTTER}`}>
          <div className="flex items-end justify-between mb-12 pb-5 border-b border-[#e8e8e8]">
            <div>
              <p className="text-[10px] font-body tracking-[0.35em] uppercase text-[#d4952a] mb-2">Most Wanted</p>
              <h2 className="font-display text-3xl md:text-4xl font-light text-[#111]">Trending Now</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
            {displayTrending.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ── Philosophy strip ──────────────────────────────────── */}
      <section className="relative pt-28 pb-36 overflow-hidden bg-[#06090c]">
        <div className="absolute inset-0 bg-[#1a4d33]/20" />
        <div className="absolute inset-0 opacity-10"
          style={{ background: 'radial-gradient(ellipse 60% 80% at 50% 50%, #d4952a33, transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-36 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, #06090c)' }} />
        <div className={`relative w-full ${GUTTER} max-w-4xl mx-auto text-center`}>
          <p className="text-[10px] font-body tracking-[0.35em] uppercase text-[#d4952a] mb-5">Our Philosophy</p>
          <h2 className="font-display text-4xl md:text-5xl font-light text-[#f0ebe0] mb-7 leading-tight">
            Quality over quantity.<br />Always.
          </h2>
          <p className="text-base text-[#9ca3af] font-body leading-relaxed mb-10">
            Maravelle doesn't list everything — we list what's worth owning. Every product is
            reviewed by our editors for quality, craftsmanship, and lasting value. No fast
            fashion. No compromise.
          </p>
          <Button asChild variant="outline" size="lg">
            <Link to="/about">About Maravelle</Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
